/* eslint-disable @typescript-eslint/no-unused-vars */
import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { ModalContext } from '../contexts';

/**
 * Redux slice for managing modal state using Redux Toolkit's `createSlice`.
 *
 * This slice handles actions related to opening, closing, and configuring modals in the application, including:
 * - Opening and closing the modal dialog
 * - Setting the modal type (e.g., alert, confirm, custom)
 * - Setting the modal action (e.g., submit, cancel)
 * - Setting the modal data (payload for the modal)
 *
 * @property {object} initialState - The initial state for the modal context.
 * @property {Function} openModal - Opens the modal dialog.
 * @property {Function} closeModal - Closes the modal dialog and resets modal state.
 * @property {Function} setModalType - Sets the type of the modal.
 * @property {Function} setModalAction - Sets the action associated with the modal.
 * @property {Function} setModalData - Sets the data payload for the modal.
 *
 * @example
 * dispatch(openModal());
 * dispatch(setModalType('Edit User'));
 * dispatch(setModalAction('VIEW'));
 * dispatch(setModalData({ id: 123 }));
 * dispatch(closeModal());
 *
 * @returns {object} The modal slice reducer and actions.
 */
const modalSlice = createSlice({
  name: 'modal',
  initialState: ModalContext,
  reducers: {
    openModal: state => {
      return {
        ...state,
        modal: true
      };
    },
    closeModal: state => {
      return {
        ...state,
        modal: false,
        modalType: '',
        modalAction: '',
        modalData: {}
      };
    },
    setModalType: (state, action: PayloadAction<string>) => {
      return {
        ...state,
        modalType: action.payload
      };
    },
    setModalAction: (state, action: PayloadAction<string>) => {
      return {
        ...state,
        modalAction: action.payload
      };
    },
    setModalData: (state, action: PayloadAction<Record<string, any>>) => {
      return {
        ...state,
        modalData: action.payload
      };
    }
  }
});

export const {
  openModal,
  closeModal,
  setModalType,
  setModalAction,
  setModalData
} = modalSlice.actions;

export default modalSlice.reducer;
